import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Drawer from "@/components/ui/drawer";
import {
  Calendar,
  Document,
  Download,
  Eye,
  Book,
  Clock,
  Employees,
  TooltipIcon,
} from "@/components/icons";
import Tooltip from "@/components/ui/tooltip";
import { PollTextarea } from "@/components/ui/poll-textarea";
import { PollRadio } from "@/components/ui/poll-radio";
import { PollCheckbox } from "@/components/ui/poll-checkbox";
import { PollStar } from "@/components/ui/poll-star";
import { PollNumbers } from "@/components/ui/poll-numbers";
import type { PollDetail } from "../../types/polls.types";
import { pollsApi } from "../../services/polls.api";
import styles from "./PollDetailDrawer.module.scss";
import previewStyles from "../PollPreviewStep/PollPreviewStep.module.scss";

interface PollDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  pollId: string | null;
}

const PollDetailDrawer: React.FC<PollDetailDrawerProps> = ({
  isOpen,
  onClose,
  pollId,
}) => {
  const { t } = useTranslation();
  const [poll, setPoll] = useState<PollDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isOpen || !pollId) {
      setPoll(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    pollsApi
      .getDetailById(pollId)
      .then((data) => {
        if (!cancelled) setPoll(data);
      })
      .catch(() => {
        if (!cancelled) setPoll(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, pollId]);

  const handleDownload = () => {
    if (!poll) return;
    const rows: string[][] = [
      [t("polls.detail.exportPollName"), poll.pollName],
      [t("polls.detail.exportPublishDate"), poll.publishDate],
      [t("polls.detail.exportRelatedEducation"), poll.relatedEducation],
      [t("polls.detail.exportQuestionCount"), String(poll.questions.length)],
      [],
      [t("polls.detail.exportQuestion"), t("polls.detail.exportType")],
    ];
    const questionTypeLabel = (type: string): string => {
      switch (type) {
        case "text":
          return t("polls.questionType.text");
        case "single_choice":
          return t("polls.questionType.singleChoice");
        case "multiple_choice":
          return t("polls.questionType.multipleChoice");
        case "dropdown":
          return t("polls.questionType.dropdown");
        case "rating":
          return t("polls.questionType.rating");
        case "scale":
          return t("polls.questionType.scale");
        default:
          return type;
      }
    };
    poll.questions.forEach((q, i) => {
      rows.push([`${i + 1}. ${q.questionText}`, questionTypeLabel(q.questionType)]);
    });
    const BOM = "\uFEFF";
    const csv = BOM + rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `anket-detayi-${poll.pollName.replace(/[^a-z0-9\u00C0-\u024F]/gi, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      headerIcon={<Eye width={24} height={24} color="#14181F" />}
      title={t("polls.detail.title")}
      position="right"
      width="640px"
      headerActions={
        poll ? (
          <button
            type="button"
            className="drawer__header-action"
            onClick={handleDownload}
            aria-label={t("common.download")}
          >
            <Download width={20} height={20} color="#34314A" />
          </button>
        ) : undefined
      }
    >
      <div className={styles.drawerContent}>
        {loading && (
          <div className={styles.loading}>
            {t("common.loading")}
          </div>
        )}
        {!loading && !poll && (
          <div className={styles.error}>
            {t("polls.detail.notFound")}
          </div>
        )}
        {!loading && poll && (
          <>
            <h4 className={styles.educationName}>{poll.relatedEducation}</h4>
            <div className={styles.infoCard}>
              <div className={styles.detailList}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailIcon}>
                      <Calendar width={20} height={20} />
                    </span>
                    <span className={styles.detailLabel}>
                      {t("polls.detail.publishDate")}
                    </span>
                    <span className={styles.detailValue}>{poll.publishDate}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailIcon}>
                      <Book
                        width={20}
                        height={20}
                        color="#707a8f"
                      />
                    </span>
                    <span className={styles.detailLabel}>
                      {t("polls.detail.relatedEducation")}
                    </span>
                    <span className={styles.detailValue}>
                      {poll.relatedEducation}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailIcon}>
                      <Document width={20} height={20} />
                    </span>
                    <span className={styles.detailLabel}>
                      {t("polls.detail.questionCount")}
                    </span>
                    <span className={styles.detailValue}>
                      {poll.questions.length}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailIcon}>
                      <Employees width={20} height={20} />
                    </span>
                    <span className={styles.detailLabel}>
                      {t("polls.detail.participationCount")}
                    </span>
                    <span className={styles.detailValueWithInfo}>
                      {poll.completedAttendees}/{poll.totalAttendees}
                      <Tooltip
                        content={t("polls.detail.participationCountTooltip")}
                        position="top"
                      >
                        <span className={styles.infoIcon}>
                          <TooltipIcon width={16} height={16} />
                        </span>
                      </Tooltip>
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailIcon}>
                      <Clock width={20} height={20} />
                    </span>
                    <span className={styles.detailLabel}>
                      {t("polls.detail.remainingTime")}
                    </span>
                    <span className={styles.detailValue}>
                      {poll.remainingTime}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailIcon}>
                      <Calendar width={20} height={20} />
                    </span>
                    <span className={styles.detailLabel}>
                      {t("polls.detail.startDate")}
                    </span>
                    <span className={styles.detailValue}>{poll.startDate}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailIcon}>
                      <Calendar width={20} height={20} />
                    </span>
                    <span className={styles.detailLabel}>
                      {t("polls.detail.endDate")}
                    </span>
                    <span className={styles.detailValue}>{poll.endDate}</span>
                  </div>
                </div>
            </div>

            <section className={styles.descriptionSection}>
              <h4 className={styles.descriptionTitle}>
                {t("polls.detail.description")}
              </h4>
              <p className={styles.pollDescription}>{poll.description}</p>
            </section>

            <div className={styles.answersSection}>
              <h4 className={styles.pollName}>{poll.pollName}</h4>
              <div className={styles.answersWrapper}>
                <div className={previewStyles.questionsList}>
                  {poll.questions.map((question, index) => {
                    const questionNum = index + 1;
                    const questionLabel = (
                      <p className={previewStyles.questionText}>
                        {questionNum}. {question.questionText}
                        {question.questionType === "multiple_choice" && (
                          <span className={previewStyles.questionHint}>
                            {" "}
                            ({t("createPoll.preview.multipleChoiceHint")})
                          </span>
                        )}
                      </p>
                    );
                    if (question.questionType === "text") {
                      return (
                        <div key={question.id} className={previewStyles.questionItem}>
                          {questionLabel}
                          <PollTextarea
                            value=""
                            readOnly
                            disabled
                            rows={4}
                          />
                        </div>
                      );
                    }
                    if (question.questionType === "multiple_choice" && question.options?.length) {
                      return (
                        <div key={question.id} className={previewStyles.questionItem}>
                          {questionLabel}
                          <div className={previewStyles.optionsList}>
                            {question.options.map((opt, optIndex) => (
                              <PollCheckbox
                                key={optIndex}
                                label={opt}
                                checked={false}
                                disabled
                                onChange={() => {}}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    }
                    if (
                      (question.questionType === "single_choice" || question.questionType === "dropdown") &&
                      question.options?.length
                    ) {
                      return (
                        <div key={question.id} className={previewStyles.questionItem}>
                          {questionLabel}
                          <div className={previewStyles.optionsList}>
                            {question.options.map((opt, optIndex) => (
                              <PollRadio
                                key={optIndex}
                                name={`q-${question.id}`}
                                label={opt}
                                value={String(optIndex)}
                                checked={false}
                                disabled
                                onChange={() => {}}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    }
                    if (question.questionType === "rating") {
                      const max = question.options?.length ? Math.max(1, question.options.length) : 5;
                      return (
                        <div key={question.id} className={previewStyles.questionItem}>
                          {questionLabel}
                          <PollStar
                            value={0}
                            maxStars={max}
                            size={32}
                            disabled
                            onChange={() => {}}
                          />
                        </div>
                      );
                    }
                    if (question.questionType === "scale") {
                      const max = question.options?.length ? question.options.length : 5;
                      return (
                        <div key={question.id} className={previewStyles.questionItem}>
                          {questionLabel}
                          <PollNumbers
                            value={0}
                            min={1}
                            max={max}
                            disabled
                            onChange={() => {}}
                            labelMin={t("createPoll.preview.scaleMin")}
                            labelMax={t("createPoll.preview.scaleMax")}
                          />
                        </div>
                      );
                    }
                    return (
                      <div key={question.id} className={previewStyles.questionItem}>
                        {questionLabel}
                        <PollTextarea value="" readOnly disabled rows={1} />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
};

export default PollDetailDrawer;
